import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import { Eye } from 'lucide-react';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

interface LatestCertificateCardProps {
    employee: Employee;
}

function LatestCertificateCard({ employee }: LatestCertificateCardProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;

    const latestCertificate = useMemo(() => {
        const certificates = employee.certificates || [];
        if (certificates.length === 0) return null;

        return certificates
            .filter((cert) => cert.startDate)
            .sort((a, b) => new Date(b.startDate!).getTime() - new Date(a.startDate!).getTime())[0];
    }, [employee.certificates]);

    const getCertificateLogo = (title: string) => {
        const lowerTitle = title.toLowerCase();
        if (lowerTitle.includes('aws')) return '☁️';
        if (lowerTitle.includes('microsoft') || lowerTitle.includes('azure')) return '🔷';
        if (lowerTitle.includes('google')) return '🔍';
        if (lowerTitle.includes('scrum')) return '🏃‍♂️';
        if (lowerTitle.includes('docker')) return '🐳';
        if (lowerTitle.includes('kubernetes')) return '⚙️';
        return '🏆';
    };

    if (!latestCertificate) {
        return null;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                background: 'transparent',
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                borderLeft: `4px solid ${theme.palette.secondary.main}`,
                mb: 3,
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    bgcolor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.secondary.main, 0.2)
                            : alpha(theme.palette.secondary.main, 0.08),
                },
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, opacity: 0.8 }}>
                {langStrings.newest} {langStrings.certificate}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                    component="img"
                    src="/certificates/certificate-placeholder.svg"
                    alt="Certificate"
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                />

                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {latestCertificate.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                        {latestCertificate.startDate
                            ? new Date(latestCertificate.startDate).toLocaleDateString('de-DE')
                            : ''}
                    </Typography>

                    {/* Additional certificate info */}
                    <Typography
                        variant="caption"
                        sx={{
                            opacity: 0.6,
                            display: 'block',
                            fontSize: '0.7rem',
                        }}
                    >
                        AWS • 3 Tage • 150€
                    </Typography>
                </Box>
            </Box>

            <Button
                variant="outlined"
                size="small"
                startIcon={<Eye size={16} />}
                sx={{
                    textTransform: 'none',
                    borderColor: alpha(theme.palette.secondary.main, 0.5),
                    color: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    '&:hover': {
                        borderColor: theme.palette.secondary.main,
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.secondary.main,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                    },
                    transition: 'all 0.2s ease',
                }}
            >
                {langStrings.viewDetails}
            </Button>
        </Paper>
    );
}

export default LatestCertificateCard;
